/**
 * Imported artwork, put onto the page.
 *
 * Everything that arrived as pixels is uploaded the way any picture is, so it
 * lives with the design and reaches the press file like anything else. Text
 * that kept its font becomes text. Outlines become outlines. The order, the
 * names, the hidden ones and the groups are the ones the file had, because a
 * shop that recognises its own layer list can work with what it imported.
 *
 * Every imported layer is fixed: it is the shop's artwork on the shop's
 * template, and the customer is not meant to take it apart. Any of them can be
 * handed back on the template screen, one tick each.
 */

import { api, signedIn } from '../api';
import { registerAssets, toDocumentRef, type Asset } from '../assets';
import { keepPicture } from '../local';
import { newId } from '../state/actions';
import { ensureStored } from '../state/persist';
import { useEditor } from '../state/store';
import { importSvg } from '../svg/import';
import type { Layer, Page, TrimboxDocument } from '../model/types';
import { mm, type ImportDoc, type ImportPiece } from './types';

/** How the page is fitted when the file is not the size of the template. */
export type Fitting = 'scale' | 'place';

/**
 * Puts an imported file onto the current page.
 *
 * @param doc     What was read from the file.
 * @param chosen  The pieces to bring in, by name path.
 * @param fitting Whether to scale the artwork to the page or keep its own size.
 * @returns What could not be brought in, if anything.
 */
export async function placeImport( doc: ImportDoc, chosen: Set<string>, fitting: Fitting ): Promise<string[]> {
  const store = useEditor.getState();
  const page = store.page();

  if ( ! page ) return [ 'There is no page to import onto.' ];

  /*
   * One scale for the whole file, worked out from the page it is going onto.
   * Scaling each layer to fit its own box would pull the design apart; the
   * artwork keeps its proportions and sits in the middle of the page.
   */
  const scale =
    fitting === 'scale' && doc.width > 0 && doc.height > 0
      ? Math.min( page.size.w / doc.width, page.size.h / doc.height )
      : 1;

  const offsetX = ( page.size.w - doc.width * scale ) / 2;
  const offsetY = ( page.size.h - doc.height * scale ) / 2;

  const trouble: string[] = [];
  const assets: Asset[] = [];

  const build = async ( piece: ImportPiece, path: string ): Promise<Layer | null> => {
    const here = path ? `${ path } / ${ piece.name }` : piece.name;

    if ( piece.kind !== 'group' && ! chosen.has( here ) ) return null;

    const box = {
      x: mm( offsetX + piece.box.x * scale ),
      y: mm( offsetY + piece.box.y * scale ),
      w: mm( piece.box.w * scale ),
      h: mm( piece.box.h * scale ),
    };

    const common = {
      id: newId( piece.kind === 'text' ? 't' : 'i' ),
      name: piece.name.slice( 0, 120 ),
      ...box,
      rotation: 0,
      opacity: piece.opacity,
      locked: false,
      hidden: piece.hidden,
      // The shop's artwork, on the shop's template.
      fixed: true,
    };

    if ( piece.kind === 'group' ) {
      const children: Layer[] = [];

      for ( const child of piece.children ?? [] ) {
        const built = await build( child, here );

        if ( built ) children.push( built );
      }

      if ( ! children.length ) return null;

      return { ...common, id: newId( 'g' ), type: 'group', children } as Layer;
    }

    if ( piece.kind === 'text' && piece.text ) {
      return {
        ...common,
        type: 'text',
        text: piece.text.words,
        font: {
          family: piece.text.family,
          weight: piece.text.bold ? 700 : 400,
          style: piece.text.italic ? 'italic' : 'normal',
          size: Math.max( 4, Math.round( piece.text.size * scale ) ),
        },
        direction: 'ltr',
        align: piece.text.align,
        lineHeight: 1.2,
        letterSpacing: 0,
        underline: false,
        strike: false,
        fill: { space: 'rgb', value: piece.text.colour },
        stroke: null,
      } as Layer;
    }

    if ( piece.kind === 'outlines' && piece.svg ) {
      try {
        const { layer } = importSvg( piece.svg, piece.name );

        return { ...common, ...layer, ...box, type: 'graphic' } as Layer;
      } catch ( error ) {
        trouble.push( `${ piece.name }: ${ error instanceof Error ? error.message : 'could not be read' }` );

        return null;
      }
    }

    if ( ! piece.canvas ) return null;

    const asset = await upload( piece.canvas, piece.name );

    if ( ! asset ) {
      trouble.push( `${ piece.name } could not be uploaded.` );

      return null;
    }

    assets.push( asset );

    return {
      ...common,
      type: 'image',
      asset: asset.id,
      fit: 'fill',
      crop: { x: 0, y: 0, w: 1, h: 1 },
      filters: { brightness: 0, contrast: 0, saturation: 0, greyscale: false },
    } as Layer;
  };

  const built: Layer[] = [];

  for ( const piece of doc.pieces ) {
    const layer = await build( piece, '' );

    if ( layer ) built.push( layer );
  }

  if ( ! built.length ) {
    return trouble.length ? trouble : [ 'Nothing was chosen to import.' ];
  }

  registerAssets( assets );

  const now = useEditor.getState();

  now.commit( 'Import artwork', ( document: TrimboxDocument ) => {
    const target = document.pages[ now.pageIndex ];

    if ( ! target ) return document;

    const pages = document.pages.slice();

    pages[ now.pageIndex ] = { ...target, layers: target.layers.concat( built ) } as Page;

    const known = new Set( document.assets.map( ( ref ) => ref.id ) );

    return {
      ...document,
      pages,
      assets: document.assets.concat( assets.filter( ( asset ) => ! known.has( asset.id ) ).map( toDocumentRef ) ),
    };
  } );

  return trouble;
}

/** A canvas uploaded as a picture, the same way a customer's upload is. */
async function upload( canvas: HTMLCanvasElement, name: string ): Promise<Asset | null> {
  const blob = await new Promise<Blob | null>( ( resolve ) => canvas.toBlob( resolve, 'image/png' ) );

  if ( ! blob ) return null;

  const file = new File( [ blob ], `${ name.replace( /[^\w\- ]+/g, '' ).slice( 0, 60 ) || 'layer' }.png`, { type: 'image/png' } );

  try {
    return signedIn() ? await api.upload( await ensureStored(), file ) : await keepPicture( file );
  } catch ( error ) {
    return null;
  }
}

/** Every piece's name path, for the list the shop ticks. */
export function pathsOf( pieces: ImportPiece[], path = '' ): string[] {
  return pieces.flatMap( ( piece ) => {
    const here = path ? `${ path } / ${ piece.name }` : piece.name;

    return piece.kind === 'group' ? pathsOf( piece.children ?? [], here ) : [ here ];
  } );
}
